/**
 * Hook for filtering and searching GitHub PRs
 */

import { useMemo, useState, useCallback } from 'react';
import type { PRData, PRReviewResult } from '../../../../preload/api/modules/github-api';
import type { NewCommitsCheck } from '../../../../preload/api/modules/github-api';

export type PRStatusFilter =
  | 'all'
  | 'reviewing'
  | 'not_reviewed'
  | 'reviewed'
  | 'posted'
  | 'changes_requested'
  | 'ready_to_merge'
  | 'ready_for_followup';

export interface PRFilterState {
  searchQuery: string;
  contributors: string[];
  statuses: PRStatusFilter[];
}

interface PRReviewInfo {
  isReviewing: boolean;
  result: PRReviewResult | null;
  newCommitsCheck?: NewCommitsCheck | null;
}

const DEFAULT_FILTERS: PRFilterState = {
  searchQuery: '',
  contributors: [],
  statuses: [],
};

/**
 * Determine the computed status of a PR based on its review state
 */
function getPRComputedStatus(
  reviewInfo: PRReviewInfo | null
): PRStatusFilter {
  // Check if currently reviewing (highest priority)
  if (reviewInfo?.isReviewing) {
    return 'reviewing';
  }

  if (!reviewInfo?.result) {
    return 'not_reviewed';
  }

  const result = reviewInfo.result;
  const hasPosted = Boolean(result.reviewId) || Boolean(result.hasPostedFindings);
  // Use overallStatus from review result as source of truth, fallback to severity check
  const hasBlockingFindings =
    result.overallStatus === 'request_changes' ||
    result.findings?.some(f => f.severity === 'critical' || f.severity === 'high');
  const hasNewCommits = reviewInfo.newCommitsCheck?.hasNewCommits;
  // Only count commits that happened AFTER findings were posted for follow-up status
  const hasCommitsAfterPosting = reviewInfo.newCommitsCheck?.hasCommitsAfterPosting;

  // Check for ready for follow-up first (highest priority after posting)
  // Must have new commits that happened AFTER findings were posted
  if (hasPosted && hasNewCommits && hasCommitsAfterPosting) {
    return 'ready_for_followup';
  }

  // Posted with blocking findings
  if (hasPosted && hasBlockingFindings) {
    return 'changes_requested';
  }

  // Posted without blocking findings
  if (hasPosted) {
    return 'ready_to_merge';
  }

  // Has review result but not posted yet
  return 'reviewed';
}

export function usePRFiltering(
  prs: PRData[],
  getReviewStateForPR: (prNumber: number) => PRReviewInfo | null
) {
  const [filters, setFiltersState] = useState<PRFilterState>(DEFAULT_FILTERS);

  // Derive unique contributors from PRs
  const contributors = useMemo(() => {
    const authorSet = new Set<string>();
    prs.forEach(pr => {
      if (pr.author?.login) {
        authorSet.add(pr.author.login);
      }
    });
    return Array.from(authorSet).sort((a, b) =>
      a.toLowerCase().localeCompare(b.toLowerCase())
    );
  }, [prs]);

  // Filter PRs based on current filters
  const filteredPRs = useMemo(() => {
    return prs.filter(pr => {
      // Search filter - matches title or body
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesTitle = pr.title.toLowerCase().includes(query);
        const matchesBody = pr.body?.toLowerCase().includes(query);
        const matchesNumber = pr.number.toString().includes(query);
        if (!matchesTitle && !matchesBody && !matchesNumber) {
          return false;
        }
      }

      // Contributors filter (multi-select)
      if (filters.contributors.length > 0) {
        const authorLogin = pr.author?.login;
        if (!authorLogin || !filters.contributors.includes(authorLogin)) {
          return false;
        }
      }

      // Status filter (multi-select)
      if (filters.statuses.length > 0) {
        const reviewInfo = getReviewStateForPR(pr.number);
        const computedStatus = getPRComputedStatus(reviewInfo);

        // Check if PR matches any of the selected statuses
        const matchesStatus = filters.statuses.some(status => {
          // Special handling: 'posted' should match any posted state
          if (status === 'posted') {
            const hasPosted = reviewInfo?.result?.reviewId || reviewInfo?.result?.hasPostedFindings;
            return hasPosted;
          }
          return computedStatus === status;
        });

        if (!matchesStatus) {
          return false;
        }
      }

      return true;
    });
  }, [prs, filters, getReviewStateForPR]);

  // Filter setters
  const setSearchQuery = useCallback((query: string) => {
    setFiltersState(prev => ({ ...prev, searchQuery: query }));
  }, []);

  const setContributors = useCallback((contributors: string[]) => {
    setFiltersState(prev => ({ ...prev, contributors }));
  }, []);

  const setStatuses = useCallback((statuses: PRStatusFilter[]) => {
    setFiltersState(prev => ({ ...prev, statuses }));
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.searchQuery !== '' ||
      filters.contributors.length > 0 ||
      filters.statuses.length > 0
    );
  }, [filters]);

  return {
    filteredPRs,
    contributors,
    filters,
    setSearchQuery,
    setContributors,
    setStatuses,
    clearFilters,
    hasActiveFilters,
  };
}
